/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
const mdSize = {height: 11, borderRadius: 'base'}

export default {
    sizes: {
        md: {
            field: {...mdSize, px: 3},
            addon: mdSize
        }
    },
    baseStyle: (props) => ({
        field: {
            flex: 1,
            mr: 2,
            border: 'none',
            borderRadius: 0,
            borderBottom: '1px solid',
            borderColor: 'gray.900',
            paddingRight: '40px',
            paddingLeft: 0,
            marginRight: 0,
            _hover: {
                borderColor: 'gray.900'
            },
            _focusVisible: {
                borderColor: 'gray.900'
            },
            _invalid: {
                borderColor: 'dangerText'
            }
        }
    }),
    variants: {
        outline: {
            field: {
                border: 'none',
                borderBottom: '1px solid',
                borderRadius: 0,
                borderColor: 'gray.700',
                lineHeight: '18px',
                _hover: {
                    borderColor: 'gray.700'
                },
                _focusVisible: {
                    borderColor: 'gray.700',
                    boxShadow: 'none'
                },
                _invalid: {
                    borderColor: 'dangerText',
                    boxShadow: 'none'
                },
                _disabled: {
                    opacity: 1,
                    color: 'gray.700',
                    background: 'grey.300'
                }
            }
        },
        filled: {
            // we use filled variant for
            // search input
            field: {
                borderColor: 'gray.600',
                backgroundColor: 'gray.100',
                _focus: {
                    backgroundColor: 'white'
                },
                _hover: {
                    backgroundColor: 'gray.100',
                    _focus: {
                        backgroundColor: 'white'
                    }
                },
                _placeholder: {
                    color: 'gray.700'
                }
            }
        },
        calendarCustom: {
            field: {
                border: 'none',
                borderBottom: '1px solid',
                borderRadius: 0,
                borderColor: 'gray.700',
                _hover: {
                    borderColor: 'gray.500'
                },
                _focusVisible: {
                    borderColor: 'gray.500',
                    boxShadow: 'none'
                },
                _invalid: {
                    borderColor: 'dangerText',
                    boxShadow: 'none'
                },
                _disabled: {
                    opacity: 1,
                    color: 'gray.700',
                    background: 'grey.300'
                },
                '&::-webkit-calendar-picker-indicator': {
                    bg: `transparent url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M10.6667 1.3335V3.3335M5.33333 1.3335V3.3335M14 6.00016H2M2 14.6668H14V2.66683H2V14.6668Z' stroke='black' stroke-linecap='square'/%3E%3C/svg%3E") no-repeat center center/16px`,
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer',
                    opacity: 1
                },
                '&::-webkit-date-and-time-value': {
                    textAlign: 'left'
                }
            }
        },
        priceRefinement: (props) => ({
            field: {
                ...(props?.theme?.textStyles?.bodyXsRegular || {}),
                fontSize: {base: 'webLg', lg: 'webMd'},
                lineHeight: '20px',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderRadius: 0,
                borderColor: 'webTheme.strokeTertiary',
                minHeight: '40px',
                maxWidth: '80px',
                paddingLeft: 2,
                paddingRight: 2,
                _hover: {
                    borderColor: 'webTheme.strokeTertiary'
                },
                _focusVisible: {
                    borderColor: 'webTheme.strokeTertiary',
                    boxShadow: 'none'
                }
            }
        }),
        findInStoreSearch: (props) => ({
            field: {
                ...(props?.theme?.textStyles?.bodySmRegular || {}),
                fontSize: {base: 'webLg', lg: 'webMd'},
                pl: 9,
                borderBottom: '1px solid',
                borderColor: 'webTypo.textPrimary',
                borderRadius: 0,
                _placeholder: {color: 'webTypo.textTertiary'}
            }
        }),
        'quantity-picker': (props) => ({
            field: {
                ...(props?.theme?.textStyles?.bodyXsRegular || {}),
                textAlign: 'center',
                padding: 0,
                height: {base: '44px', lg: 6},
                width: {base: '44px', lg: 6},
                minWidth: 6,
                marginRight: 0,
                flex: 'unset',
                borderColor: 'webTheme.strokeTertiary',
                borderTopColor: 'webTheme.strokeTertiary !important',
                borderBottom: '1px solid',
                borderTop: '1px solid',
                borderRadius: 0,
                _hover: {
                    borderColor: 'webTheme.strokeTertiary',
                },
                _focusVisible: {
                    borderColor: 'webTheme.strokeTertiary',
                    boxShadow: 'none'
                }
            }
        }),
        webTextInput: (props) => ({
            field: {
                ...(props?.theme?.textStyles?.bodyMdRegular || {}),
                width: '100%',
                height: '48px',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderRadius: 0,
                borderColor: 'webTypo.textDisabled',
                paddingLeft: 2,
                paddingRight: 2,
                paddingTop: '22px',
                paddingBottom: '4px',
                bg: 'webTheme.fieldDefault',
                color: 'webTypo.textPrimary',
                _placeholder: {
                    color: 'transparent'
                },
                _hover: {
                    borderColor: 'webTypo.textDisabled'
                },
                _focusVisible: {
                    borderColor: 'webTheme.strokeInteractive',
                    boxShadow: 'inset 0 0 0 1px var(--chakra-colors-webTheme-strokeInteractive)'
                },
                _invalid: {
                    borderColor: 'webTheme.strokeWarning',
                    boxShadow: 'none',
                    _focusVisible: {
                        borderColor: 'webTheme.strokeWarning',
                        boxShadow: 'inset 0 0 0 1px var(--chakra-colors-webTheme-supportError)'
                    }
                },
                _disabled: {
                    opacity: 1,
                    bg: 'webTheme.fieldDisabled',
                    borderColor: 'webTypo.textDisabled',
                    color: 'webTypo.textPrimary',
                    cursor: 'not-allowed',
                    _hover: {
                        borderColor: 'webTypo.textDisabled'
                    }
                },
                _readOnly: {
                    opacity: 1,
                    bg: 'webTheme.fieldDisabled',
                    borderColor: 'webTypo.textDisabled',
                    color: 'webTypo.textPrimary',
                    cursor: 'default',
                    _hover: {
                        borderColor: 'webTypo.textDisabled'
                    }
                },
                '&::-webkit-calendar-picker-indicator': {
                    bg: `transparent url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M10.6667 1.3335V3.3335M5.33333 1.3335V3.3335M14 6.00016H2M2 14.6668H14V2.66683H2V14.6668Z' stroke='black' stroke-linecap='square'/%3E%3C/svg%3E") no-repeat center center/20px`,
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    opacity: 1,
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)'
                }
            }
        })
    }
}
